//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { IOnChangesObject } from 'angular';

import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import {
  IToastNotification,
  ToastType,
} from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import curalateModule from 'app/curalate.module';
import newToastTemplate from '@legacy-components/common/toast-notifications/templates/new-toast.html';
import plainToastTemplate from '@legacy-components/common/toast-notifications/templates/plain-toast.html';
import infoToastTemplate from '@legacy-components/common/toast-notifications/templates/info-toast.html';

export interface IOnNotified {
  notification: IToastNotification;
}

export interface IOnNotificationClose {
  notificationId: string;
}

export interface IToastNotificationService {
  close: (notificationId: string) => void;
  error: (notification: IToastNotification) => void;
  success: (notification: IToastNotification) => void;
  warning: (notification: IToastNotification) => void;
}

export class ToastNotifications {
  public notifications: IToastNotification[];
  public onNotified: (data: IOnNotified) => void;
  public notificationsToClose: string[];
  public onNotificationClose: (data: IOnNotificationClose) => void;

  constructor(
    private toastNotificationService,
    private $templateCache,
  ) {
    $templateCache.put(
      ALTERNATE_TOASTS.NEW_TOAST,
      newToastTemplate,
    );
    $templateCache.put(
      ALTERNATE_TOASTS.PLAIN_TOAST,
      plainToastTemplate,
    );
    $templateCache.put(
      ALTERNATE_TOASTS.INFO_TOAST,
      infoToastTemplate,
    );
  }

  public $onChanges(changesObj: IOnChangesObject) {
    const { notifications, notificationsToClose } = changesObj;

    if (notifications) {
      const currentNotifications = notifications.currentValue;
      currentNotifications?.forEach((notification: IToastNotification) => {
        if (notification.type === ToastType.ERROR) {
          this.toastNotificationService.error(notification);
        } else if (notification.type === ToastType.WARNING) {
          this.toastNotificationService.warning(notification);
        } else {
          // Default to success when no type is specified
          this.toastNotificationService.success(notification);
        }
        this.onNotified({ notification });
      });
    }

    if (notificationsToClose) {
      const currentNotificationsToClose = notificationsToClose.currentValue;
      currentNotificationsToClose?.forEach((notificationId: string) => {
        this.toastNotificationService.close(notificationId);
        this.onNotificationClose({ notificationId });
      });
    }
  }
}

curalateModule.component('toastNotifications', {
  bindings: {
    notifications: '<',
    notificationsToClose: '<',
    onNotificationClose: '&',
    onNotified: '&',
  },
  controller: [
    'ToastNotificationService',
    '$templateCache',
    ToastNotifications,
  ],
});
